import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { Subscription, firstValueFrom } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { MatSort, Sort } from '@angular/material/sort';

import { ConfirmDialogComponent, ConfirmDialogData } from '../../../shared/confirm-dialog/confirm-dialog.component';
import {
  MessstelleDuplizierenDialogComponent,
  MessstelleDuplizierenDialogData
} from './messstelle-duplizieren-dialog/messstelle-duplizieren-dialog.component';

import {
  AbgleichAuswahl,
  AbgleichEintrag,
  AbgleichErgebnis,
  Anlagenteil,
  StrukturAenderung,
  FELD_KENNZEICHNUNG,
  Messstelle,
  MessstelleDuplikatErgebnis,
  MessstelleKategorie,
  MessstellenListeDetail,
  MessstellenSammelErgebnis,
  MessstelleDetail,
  PruefpunktErgebnis,
  normalizePruefpunktErgebnis,
  Pruefergebnis,
  SignalStatus,
  SIGNAL_STATUS_OPTIONEN,
  kennzeichnungLabel,
  normalizePruefergebnis,
  normalizeSignalStatus,
  pruefergebnisCss,
  pruefergebnisText,
  REGEL_OPERATOREN,
  RegelOperator,
  SammelRegelErgebnis,
  SammelZuordnungErgebnis,
  SammelZuordnungRegeln,
  signalStatusCss,
  signalStatusText,
  messstelleFeldMitAliassen,
  standardFelder,
  ListenSpalte,
  ListenStruktur,
  strukturSpalten,
  kennzeichnungsSpalte,
  pruefeSpaltenWert,
  isoAlsDatum
} from '../../../../Models/messstelle.model';

import {
  StrukturBearbeitenDialogComponent,
  StrukturBearbeitenDialogData
} from '../listen-struktur/struktur-bearbeiten-dialog/struktur-bearbeiten-dialog.component';
import {
  AlsVorlageDialogComponent,
  AlsVorlageDialogData,
  AlsVorlageDialogErgebnis
} from '../listen-struktur/als-vorlage-dialog/als-vorlage-dialog.component';

import { Projekt } from '../../../../Models/projekt.model';

import { Datenquelle } from '../../../../Models/offline.model';

import { InbetriebnahmeService } from '../../../../Services/inbetriebnahme.service';
import { InbetriebnahmeDataService } from '../../../../Services/offline/inbetriebnahme-data.service';
import { ProjektService } from '../../../../Services/projekt.service';
import { AuthService } from '../../../../api-authorization/authorize.service';


type MessstellenStatusFilter =
  | 'alle'
  | 'nichtGestartet'
  | 'offen'
  | 'geprueft';


interface MessstellenFilter {
  search: string;
  kategorie: string;
  status: MessstellenStatusFilter;
  /** '' = alle. Sonst der Zahlwert des Signalstatus als Text. */
  signalStatus: string;
  /** '' = alle, '__keiner__' = ohne Zuordnung, sonst die Id als Text. */
  anlagenteil: string;
}


/** Was per Regel gesammelt vergeben wird. */
type SammelZuordnungArt = 'kategorie' | 'anlagenteil';


/**
 * Eine Regel im Bereich „Kategorien/Anlagenteile zuordnen“ – dieselben
 * Angaben wie im Importdialog. Je nach Art zählt Kategorie oder Anlagenteil.
 */
interface SammelRegelentwurf {
  feld: string;
  operator: RegelOperator;
  wert: string;
  kategorieId: number | null;
  anlagenteilName: string;

  /** Ergebnis aus der letzten Vorschau. Null = noch nicht gerechnet. */
  ergebnis: SammelRegelErgebnis | null;

  zeigeBeispiele: boolean;
}


/**
 * Alle Messstellen einer importierten Liste als Tabelle.
 *
 * Die Spalten stehen erst zur Laufzeit fest – sie kommen aus der importierten Datei.
 * Zusätzlich kann eine noch nicht zugeordnete Inbetriebnahme auf dieser Seite
 * nachträglich einem passenden Projekt zugeordnet werden.
 */
/**
 * Was über einer Excel-Spalte steht.
 *
 * Rein für die Anzeige. Die Liste selbst kennt nur ihre Excel-Spaltennamen –
 * die fachliche Beschriftung entsteht erst hier aus dem fieldMapping.
 */
interface SpaltenKopf {
  /** Erste Zeile: das Standardfeld, sonst der Excel-Name. */
  titel: string;

  /** Zweite, kleine Zeile – null, wenn sie nur den Titel wiederholen würde. */
  herkunft: string | null;

  /** Tooltip; leer, wenn es nichts zu erklären gibt. */
  hinweis: string;
}


@Component({
  selector: 'app-messstellen-liste',
  templateUrl: './messstellen-liste.component.html',
  styleUrls: ['./messstellen-liste.component.css']
})
export class MessstellenListeComponent implements OnInit, OnDestroy {

  @ViewChild(MatPaginator)
  set matPaginator(
    paginator: MatPaginator
  ) {

    if (paginator) {
      this.paginator = paginator;
      this.dataSource.paginator = paginator;
    }
  }

  @ViewChild(MatSort)
  set matSort(sort: MatSort) {
    if (sort) {
      this.dataSource.sort = sort;
    }
  }

  private paginator: MatPaginator | null = null;


  // ========================================================
  // MOBILE ANSICHT
  //
  // Unter 700 px Breite stehen die Messstellen als Karten statt als
  // Tabelle. Nur die Darstellung wechselt: Filter, Reihenfolge und
  // Blättern bleiben die der MatTableDataSource.
  // ========================================================

  /** Ab hier gilt die Karten-Ansicht. Muss zur Media-Query im CSS passen. */
  private static readonly MOBILE_QUERY = '(max-width: 700px)';

  istMobil = false;

  readonly mobileSeitenGroesse = 5;

  readonly mobileSeitenGroessen = [5];

  readonly desktopSeitenGroessen = [25, 50, 100, 250];

  /** Die zuletzt auf dem Desktop gewählte Seitengröße – gilt nach der Rückkehr wieder. */
  desktopSeitenGroesse = 50;

  private breakpointSub?: Subscription;

  /** Spalte der Kennzeichnung (KKS, AKZ, …) – für die Karte. */
  private kennzeichnungSpalte: string | null = null;

  /** Spalte des Langtexts, ersatzweise des Kurztexts – für die Karte. */
  private langtextSpalte: string | null = null;


  // ========================================================
  // LISTE
  // ========================================================

  liste: MessstellenListeDetail | null = null;

  isLoading = true;

  loadError = '';


  // ========================================================
  // TABELLE
  // ========================================================

  displayedColumns: string[] = [];


  // ========================================================
  // MEHRFACHAUSWAHL
  //
  // Die Auswahl hängt an Messstellen-Ids, nie an Tabellenindizes: Suche,
  // Filter, Sortierung und Seitenwechsel ändern die Reihenfolge der Zeilen,
  // nicht aber, welche Messstellen gemeint sind. Beim Nachladen der Liste
  // werden nur Ids entfernt, die es nicht mehr gibt (siehe uebernehmeListe).
  // ========================================================

  ausgewaehlteMessstellen = new Set<number>();

  /** Welche Sammelaktion gerade läuft – sperrt Auswahl und Schaltflächen. */
  sammelAktion: 'pruefen' | 'loeschen' | null = null;

  /** Fortschritt der laufenden Sammelaktion, falls sie Messstelle für Messstelle läuft. */
  sammelFortschritt: { erledigt: number; gesamt: number } | null = null;

  get anzahlAusgewaehlt(): number {
    return this.ausgewaehlteMessstellen.size;
  }

  get sammelAktionLaeuft(): boolean {
    return this.sammelAktion !== null;
  }

  istMessstelleAusgewaehlt(messstelle: Messstelle): boolean {
    return this.ausgewaehlteMessstellen.has(messstelle.id);
  }

  /** Der Klick-Stopper sitzt im Template; hier zählt nur die Messstelle. */
  toggleMessstelleAuswahl(messstelle: Messstelle): void {
    if (this.sammelAktionLaeuft) {
      return;
    }

    if (this.ausgewaehlteMessstellen.has(messstelle.id)) {
      this.ausgewaehlteMessstellen.delete(messstelle.id);
    } else {
      this.ausgewaehlteMessstellen.add(messstelle.id);
    }
  }

  auswahlAufheben(): void {
    if (!this.sammelAktionLaeuft) {
      this.ausgewaehlteMessstellen.clear();
    }
  }

  /**
   * Alle Messstellen, die zum aktuellen Filter passen – über ALLE Seiten.
   * Die Kopfzeilen-Checkbox meint genau diese Menge: wer „Nicht geprüft"
   * filtert und alles anwählt, bekommt alle ungeprüften, nicht nur die Seite.
   */
  get gefilterteMessstellen(): Messstelle[] {
    return this.dataSource.filteredData;
  }

  get alleGefiltertenAusgewaehlt(): boolean {
    const gefiltert = this.gefilterteMessstellen;

    return gefiltert.length > 0
      && gefiltert.every(m => this.ausgewaehlteMessstellen.has(m.id));
  }

  get einigeGefilterteAusgewaehlt(): boolean {
    return this.gefilterteMessstellen.some(m => this.ausgewaehlteMessstellen.has(m.id));
  }

  toggleAlleGefilterten(): void {
    if (this.sammelAktionLaeuft) {
      return;
    }

    const alleAusgewaehlt = this.alleGefiltertenAusgewaehlt;

    for (const messstelle of this.gefilterteMessstellen) {
      if (alleAusgewaehlt) {
        this.ausgewaehlteMessstellen.delete(messstelle.id);
      } else {
        this.ausgewaehlteMessstellen.add(messstelle.id);
      }
    }
  }

  get kopfCheckboxTooltip(): string {
    const anzahl = this.gefilterteMessstellen.length;

    if (this.alleGefiltertenAusgewaehlt) {
      return 'Auswahl der gefilterten Messstellen aufheben';
    }

    return this.hatAktiveFilter
      ? `Alle ${anzahl} gefilterten Messstellen auswählen (alle Seiten)`
      : `Alle ${anzahl} Messstellen auswählen (alle Seiten)`;
  }

  /** Die ausgewählten Messstellen in Listenreihenfolge, wie sie gerade vorliegen. */
  private get ausgewaehlteObjekte(): Messstelle[] {
    return (this.liste?.messstellen ?? []).filter(m => this.ausgewaehlteMessstellen.has(m.id));
  }


  // ========================================================
  // SAMMELAKTION: AUSGEWÄHLTE PRÜFEN
  //
  // Setzt alle Prüfpunkte der gewählten Messstellen auf „bestanden" – mit
  // derselben Logik wie die Einzelprüfung. Online erledigt das der Server in
  // einem Aufruf (Quittierung, Änderungsstand, Protokoll je Prüfpunkt wie bei
  // PUT pruefpunkte/{id}); ohne Verbindung läuft dieselbe Prüfung Messstelle
  // für Messstelle über den Offline-Datendienst in die Warteschlange.
  // ========================================================

  get kannSammelPruefen(): boolean {
    return this.canEdit && !this.sammelAktionLaeuft && this.anzahlAusgewaehlt > 0;
  }

  async ausgewaehltePruefen(): Promise<void> {
    if (!this.kannSammelPruefen || !this.liste) {
      return;
    }

    const ids = this.ausgewaehlteObjekte.map(m => m.id);

    if (ids.length === 0) {
      return;
    }

    const listeId = this.liste.id;

    this.sammelAktion = 'pruefen';
    this.sammelFortschritt = null;

    try {
      const ergebnis = this.istOffline
        ? await this.pruefeLokal(ids)
        : await firstValueFrom(this.service.pruefeMessstellen(listeId, ids));

      this.meldeSammelergebnis('pruefen', ergebnis);

      // Erfolgreich geprüfte abwählen; fehlgeschlagene bleiben markiert, damit
      // der Benutzer sieht, welche es betrifft, und es erneut versuchen kann.
      for (const id of [...ergebnis.erfolgreich, ...ergebnis.uebersprungen]) {
        this.ausgewaehlteMessstellen.delete(id);
      }
    } catch (err: any) {
      this.snackBar.open(
        err?.error?.message ?? err?.message ?? 'Die Prüfung konnte nicht durchgeführt werden.',
        'OK',
        { duration: 6000 }
      );
    } finally {
      this.sammelAktion = null;
      this.sammelFortschritt = null;
    }

    // Frisch laden: Fortschritt, Chips und Zähler kommen so aus demselben Stand.
    this.ladeListe(listeId);
  }

  /**
   * Offline-Fassung der Sammelprüfung: jede Messstelle wird geladen und jeder
   * noch nicht bestandene Prüfpunkt über den bestehenden Schreibweg gesetzt –
   * genau wie ein Klick auf der Detailseite. Jede Änderung landet damit in der
   * Warteschlange und wird bei Verbindung synchronisiert.
   */
  private async pruefeLokal(ids: number[]): Promise<MessstellenSammelErgebnis> {
    const ergebnis: MessstellenSammelErgebnis = {
      erfolgreich: [],
      uebersprungen: [],
      fehlgeschlagen: [],
      pruefpunkteGesetzt: 0
    };

    this.sammelFortschritt = { erledigt: 0, gesamt: ids.length };

    for (const id of ids) {
      try {
        const geladen = await firstValueFrom(this.data.getMessstelle(id));
        const detail: MessstelleDetail = geladen.daten;
        const offen = (detail.pruefpunkte ?? []).filter(
          p => normalizePruefpunktErgebnis(p.ergebnis) !== PruefpunktErgebnis.Bestanden
        );

        if ((detail.pruefpunkte ?? []).length === 0) {
          ergebnis.uebersprungen.push(id);
          continue;
        }

        for (const punkt of offen) {
          await firstValueFrom(
            this.data.updatePruefpunkt(detail, punkt, PruefpunktErgebnis.Bestanden, punkt.bemerkung ?? null)
          );
          ergebnis.pruefpunkteGesetzt++;
        }

        ergebnis.erfolgreich.push(id);
      } catch (err: any) {
        ergebnis.fehlgeschlagen.push({
          messstelleId: id,
          grund: err?.error?.message ?? err?.message ?? 'Die Prüfung konnte nicht gespeichert werden.'
        });
      } finally {
        this.sammelFortschritt = { erledigt: this.sammelFortschritt!.erledigt + 1, gesamt: ids.length };
      }
    }

    return ergebnis;
  }


  // ========================================================
  // SAMMELAKTION: AUSGEWÄHLTE LÖSCHEN
  //
  // Online-only wie das Einzellöschen; der Server löscht jede Messstelle für
  // sich mit derselben Löschlogik, sodass eine nicht löschbare Messstelle
  // die übrigen nicht aufhält.
  // ========================================================

  get kannSammelLoeschen(): boolean {
    return this.kannLoeschen && !this.sammelAktionLaeuft && this.anzahlAusgewaehlt > 0;
  }

  get sammelLoeschenTooltip(): string {
    if (this.istOffline) {
      return 'Löschen ist offline nicht möglich – die Messstellen werden auf dem Server entfernt.';
    }

    return 'Alle ausgewählten Messstellen samt Prüfpunkten, Verlauf, Fotos und Geräte-Dateien löschen';
  }

  ausgewaehlteLoeschen(): void {
    if (!this.kannSammelLoeschen || !this.liste) {
      return;
    }

    const auswahl = this.ausgewaehlteObjekte;

    if (auswahl.length === 0) {
      return;
    }

    const anzahl = auswahl.length;
    const listeId = this.liste.id;

    const mitErgebnissen = auswahl.filter(m =>
      (m.pruefpunkteErledigt ?? 0) > 0
      || this.hatPruefergebnis(m)
      || normalizeSignalStatus(m.signalStatus) !== SignalStatus.Offen
    ).length;

    // Die ersten Kennzeichnungen als Anhaltspunkt – bei vielen nur die Zahl.
    const beispiele = auswahl
      .slice(0, 5)
      .map(m => this.kennzeichnungWert(m) || `Zeile ${m.rowNumber}`);

    const details = [
      `${anzahl === 1 ? 'Messstelle' : 'Messstellen'}: ${beispiele.join(', ')}${anzahl > 5 ? ` … und ${anzahl - 5} weitere` : ''}`
    ];

    const warnung = mitErgebnissen > 0
      ? `Achtung: Für ${mitErgebnissen === 1 ? 'eine' : mitErgebnissen} der ausgewählten Messstellen sind bereits `
      + 'Prüfergebnisse erfasst. Prüfpunkte, Prüfverlauf, Fotos und Geräte-Dateien werden unwiderruflich mitgelöscht.'
      : 'Prüfpunkte, Prüfverlauf, Fotos und Geräte-Dateien der Messstellen werden mitgelöscht. '
      + 'Diese Aktion kann nicht rückgängig gemacht werden.';

    const data: ConfirmDialogData = {
      title: anzahl === 1 ? 'Ausgewählte Messstelle wirklich löschen?' : `${anzahl} ausgewählte Messstellen wirklich löschen?`,
      message: anzahl === 1
        ? `Möchtest du die ausgewählte Messstelle wirklich aus der Inbetriebnahme „${this.liste.name}“ löschen?`
        : `Möchtest du die ${anzahl} ausgewählten Messstellen wirklich aus der Inbetriebnahme „${this.liste.name}“ löschen?`,
      details,
      warning: warnung,
      confirmText: anzahl === 1 ? 'Messstelle löschen' : `${anzahl} Messstellen löschen`
    };

    this.dialog
      .open<ConfirmDialogComponent, ConfirmDialogData, boolean>(ConfirmDialogComponent, {
        data,
        width: '520px',
        maxWidth: '95vw',
        autoFocus: false
      })
      .afterClosed()
      .subscribe(async bestaetigt => {
        if (!bestaetigt || this.sammelAktionLaeuft) {
          return;
        }

        this.sammelAktion = 'loeschen';

        try {
          const ergebnis = await firstValueFrom(
            this.service.loescheMessstellen(listeId, auswahl.map(m => m.id))
          );

          this.meldeSammelergebnis('loeschen', ergebnis);

          for (const id of [...ergebnis.erfolgreich, ...ergebnis.uebersprungen]) {
            this.ausgewaehlteMessstellen.delete(id);
          }
        } catch (err: any) {
          this.snackBar.open(
            err?.error?.message ?? err?.message ?? 'Die Messstellen konnten nicht gelöscht werden.',
            'OK',
            { duration: 6000 }
          );
        } finally {
          this.sammelAktion = null;
        }

        // Frisch laden: Anzahl, Filter, Anlagenteile, Kategorien und
        // Fortschritt kommen so aus demselben Stand wie die Liste.
        this.ladeListe(listeId);
      });
  }

  /** Verständliche Zusammenfassung einer Sammelaktion – mit Gründen, falls etwas fehlschlug. */
  private meldeSammelergebnis(art: 'pruefen' | 'loeschen', ergebnis: MessstellenSammelErgebnis): void {
    const ok = ergebnis.erfolgreich.length;
    const uebersprungen = ergebnis.uebersprungen.length;
    const fehler = ergebnis.fehlgeschlagen.length;
    const teile: string[] = [];

    if (art === 'pruefen') {
      if (ok > 0) {
        teile.push(
          `${ok} ${ok === 1 ? 'Messstelle' : 'Messstellen'} geprüft`
          + (ergebnis.pruefpunkteGesetzt > 0
            ? ` (${ergebnis.pruefpunkteGesetzt} ${ergebnis.pruefpunkteGesetzt === 1 ? 'Prüfpunkt' : 'Prüfpunkte'} auf „bestanden“ gesetzt)`
            : ' – alle Prüfpunkte waren bereits bestanden')
          + (this.istOffline ? ', lokal gespeichert – wird bei Verbindung synchronisiert' : '')
        );
      }

      if (uebersprungen > 0) {
        teile.push(`${uebersprungen} ohne Prüfpunkte übersprungen`);
      }
    } else {
      if (ok > 0) {
        teile.push(`${ok} ${ok === 1 ? 'Messstelle' : 'Messstellen'} gelöscht`);
      }

      if (uebersprungen > 0) {
        teile.push(`${uebersprungen} bereits gelöscht`);
      }
    }

    if (fehler > 0) {
      const gruende = [...new Set(ergebnis.fehlgeschlagen.map(f => f.grund))];
      teile.push(`${fehler} fehlgeschlagen: ${gruende.join(' ')}`);
    }

    this.snackBar.open(teile.join(' · ') || 'Nichts zu tun.', 'OK', { duration: fehler > 0 ? 10000 : 5000 });
  }

  dataSource =
    new MatTableDataSource<Messstelle>([]);

  /**
   * Genau die Zeilen der aktuellen Seite – gefiltert und paginiert, derselbe
   * Strom, aus dem auch die Tabelle ihre Zeilen bekommt. Die Karten auf Mobile
   * rendern dadurch nie mehr als eine Seite.
   *
   * Die Tabelle bleibt dafür auch auf Mobile im DOM (nur ausgeblendet): beim
   * Entfernen würde sie `disconnect()` rufen und damit die Paginierung der
   * Datenquelle abklemmen – die Karten blieben dann auf dem alten Stand stehen.
   */
  readonly seitenMessstellen$ =
    this.dataSource.connect();


  // ========================================================
  // FILTER
  // ========================================================

  searchTerm = '';

  selectedKategorie = '';

  selectedStatus: MessstellenStatusFilter =
    'alle';

  /** '' = alle. Sonst der Zahlwert des Signalstatus als Text. */
  selectedSignalStatus = '';

  readonly signalStatusOptionen = SIGNAL_STATUS_OPTIONEN;

  kategorien: string[] = [];

  /** '' = alle, '__keiner__' = ohne Zuordnung, sonst die Id als Text. */
  selectedAnlagenteil = '';

  /**
   * ALLE Anlagenteile des Projekts (alphabetisch, vom Server) – ausschließlich
   * für die ZUORDNUNG: „Messstelle ergänzen“, Regelvorschläge, Namensauflösung.
   * Für den Filter gilt die eingeschränkte Liste `filterAnlagenteile`.
   */
  anlagenteile: Anlagenteil[] = [];

  /**
   * Optionen des Anlagenteil-FILTERS: nur Anlagenteile, die mindestens einer
   * Messstelle der aktuell geöffneten Liste zugeordnet sind – ohne Dubletten,
   * in der (alphabetischen) Reihenfolge von `anlagenteile`. Die Liste ist
   * clientseitig vollständig geladen, daher zählen alle Messstellen, nicht nur
   * die sichtbare Seite. Wird in `uebernehmeListe` neu berechnet, also nach
   * jedem Laden (Import, Löschen, Zuordnungsänderung, Regeln, Listenwechsel).
   */
  filterAnlagenteile: Anlagenteil[] = [];

  /**
   * „Ohne Zuordnung“ soll im Filter nur erscheinen, wenn tatsächlich
   * mindestens eine Messstelle der Liste kein Anlagenteil besitzt.
   */
  hatMessstellenOhneAnlagenteil = false;

  /**
   * Filteroptionen aus den tatsächlichen Listendaten ableiten und eine
   * Auswahl verwerfen, die es in dieser Liste nicht (mehr) gibt – sonst
   * bliebe ein unsichtbarer Filter aktiv.
   */
  private aktualisiereFilterAnlagenteile(): void {

    const messstellen =
      this.liste?.messstellen
      ?? [];


    const verwendeteIds =
      new Set<number>(
        messstellen
          .map(
            messstelle =>
              messstelle.anlagenteilId
          )
          .filter(
            (
              id
            ): id is number =>
              id != null
          )
      );


    this.filterAnlagenteile =
      this.anlagenteile.filter(
        anlagenteil =>
          verwendeteIds.has(
            anlagenteil.id
          )
      );


    this.hatMessstellenOhneAnlagenteil =
      messstellen.some(
        messstelle =>
          messstelle.anlagenteilId == null
      );


    if (
      this.selectedAnlagenteil === '__keiner__'
    ) {

      if (!this.hatMessstellenOhneAnlagenteil) {
        this.selectedAnlagenteil = '';
      }

    } else if (
      this.selectedAnlagenteil
      && !this.filterAnlagenteile.some(
        a => String(a.id) === this.selectedAnlagenteil
      )
    ) {

      this.selectedAnlagenteil = '';
    }
  }


  // ========================================================
  // MESSSTELLE ERGÄNZEN
  //
  // Eine importierte Liste ist kein Abschluss: vor Ort tauchen
  // Messstellen auf, die in keiner Revision stehen.
  // ========================================================

  zeigeNeueMessstelle = false;

  /** Standardfeld -> Wert. Der Server löst daraus die Excel-Spalten auf. */
  neueWerte: { [feld: string]: string } = {};

  neueKategorieId: number | null = null;

  neueAnlagenteilId: number | null = null;

  kategorienAuswahl: MessstelleKategorie[] = [];

  speichertNeue = false;

  neueFehler = '';

  /** Erst nach dem ersten Speicherversuch werden leere Pflichtfelder markiert. */
  neueGeprueft = false;


  // ========================================================
  // ABGLEICH MIT EINER NEUEREN EXCEL-DATEI
  // ========================================================

  zeigeAbgleich = false;

  abgleichDatei: File | null = null;

  abgleichLaeuft = false;

  abgleichFehler = '';

  abgleich: AbgleichErgebnis | null = null;

  /**
   * Bearbeitungsmodus.
   *
   * Standardmäßig aus: nach der Dateiwahl soll zuerst DASTEHEN, was sich
   * geändert hat – ohne Kästchen, ohne Einzelentscheidung. Wer wirklich
   * etwas abwählen will, schaltet um.
   */
  abgleichBearbeiten = false;

  /** Zeilennummern der neuen Datei, die übernommen werden sollen. */
  gewaehlteNeue = new Set<number>();

  /**
   * Angehakte Feldänderungen, als `messstelleId::Spalte`.
   *
   * Einzelne Felder statt ganzer Messstellen: eine Revision, die den Langtext
   * korrigiert und nebenbei den Messbereich zerschiesst, soll sich zur Hälfte
   * übernehmen lassen.
   */
  gewaehlteFelder = new Set<string>();

  /** Angehakte Strukturänderungen, als `Art::Spalte`. */
  gewaehlteStruktur = new Set<string>();


  // ========================================================
  // PROJEKTZUORDNUNG
  // ========================================================

  projekte: Projekt[] = [];

  selectedProjektId: number | null = null;

  loadingProjekte = false;

  assigningProjekt = false;

  projektAssignmentError = '';


  // ========================================================
  // OFFLINE
  //
  // Suche, Filter und Blättern laufen ohnehin vollständig auf den
  // geladenen Daten (MatTableDataSource) – sie funktionieren deshalb
  // ohne jede weitere Anfrage, egal woher die Zeilen stammen.
  // ========================================================

  /** Woher die angezeigten Daten kommen. Nur für den Hinweis in der Kopfzeile. */
  quelle: Datenquelle = 'server';

  /** Stand der lokalen Daten, wenn offline gearbeitet wird. */
  standUtc: string | null = null;


  get istOffline(): boolean {
    return this.quelle === 'offline';
  }


  constructor(
    /** Entscheidet, ob vom Server oder aus dem lokalen Bestand gelesen wird. */
    private data: InbetriebnahmeDataService,

    /** Direkt für die Projektzuordnung – die ist bewusst online-only. */
    private service: InbetriebnahmeService,

    private projektService: ProjektService,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private authService: AuthService,
    private breakpointObserver: BreakpointObserver,
    private dialog: MatDialog
  ) { }

  get canEdit(): boolean {
    return this.authService.canEdit();
  }


  // ========================================================
  // MESSSTELLE LÖSCHEN
  //
  // Bewusst online-only: für ein Löschen gibt es keine
  // Offline-Synchronisation. Offline wird die Aktion gar nicht
  // erst angeboten.
  // ========================================================

  /** Messstelle, deren Löschen gerade läuft – sperrt die Schaltfläche. */
  loeschtMessstelleId: number | null = null;

  get kannLoeschen(): boolean {
    return this.canEdit && !this.istOffline;
  }


  // ========================================================
  // MESSSTELLE DUPLIZIEREN
  //
  // Bewusst online-only, wie das Löschen: die Kopie entsteht mit
  // Prüfpunkten, Verlauf, Fotos und Dateien auf dem Server. Offline gäbe
  // es dafür weder eine Identität noch einen Sync-Typ – der Knopf bleibt
  // sichtbar, ist aber ausgegraut und sagt warum.
  // ========================================================

  /** Messstelle, deren Kopie gerade entsteht – sperrt die Schaltflächen. */
  dupliziertMessstelleId: number | null = null;

  /** Frisch kopierte Messstelle – wird nach dem Nachladen angesprungen. */
  private neueMessstelleId: number | null = null;

  /** Kurz hervorgehobene Zeile bzw. Karte, damit die Kopie ins Auge fällt. */
  hervorgehobeneMessstelleId: number | null = null;

  get kannDuplizieren(): boolean {
    return this.canEdit && !this.istOffline;
  }

  get duplizierenTooltip(): string {
    return this.istOffline
      ? 'Duplizieren ist offline nicht möglich – die Kopie entsteht auf dem Server.'
      : 'Messstelle duplizieren';
  }

  messstelleDuplizieren(
    messstelle: Messstelle,
    event: Event
  ): void {

    // Der Klick gehört der Schaltfläche, nicht der Zeile bzw. Karte.
    event.stopPropagation();

    if (!this.kannDuplizieren || !this.liste || this.dupliziertMessstelleId !== null) {
      return;
    }

    const liste = this.liste;
    const listeId = liste.id;

    // Dieselbe Dublettenregel wie beim Anlegen: Groß-/Kleinschreibung und
    // Leerraum egal, geprüft gegen alle Messstellen DIESER Liste.
    const vergeben = new Set<string>();
    const spalte = this.kennzeichnungSpalte;
    if (spalte) {
      for (const m of liste.messstellen ?? []) {
        const wert = m.values?.[spalte] ?? '';
        if (wert.trim()) {
          vergeben.add(this.vergleichbar(wert));
        }
      }
    }

    const data: MessstelleDuplizierenDialogData = {
      alteKennzeichnung: this.kennzeichnungWert(messstelle),
      kennzeichnungLabel: this.kennzeichnungAnzeige,
      zeile: messstelle.rowNumber,
      pruefpunkte: messstelle.pruefpunkteGesamt ?? 0,
      vergeben,
      vergleichbar: wert => this.vergleichbar(wert),
      ausfuehren: kennzeichnung => {
        this.dupliziertMessstelleId = messstelle.id;
        return this.service
          .dupliziereMessstelle(listeId, messstelle.id, kennzeichnung)
          .pipe(finalize(() => this.dupliziertMessstelleId = null));
      }
    };

    this.dialog
      .open(MessstelleDuplizierenDialogComponent, { data, maxWidth: '520px' })
      .afterClosed()
      .subscribe((ergebnis: MessstelleDuplikatErgebnis | null | undefined) => {
        if (!ergebnis) {
          return;
        }

        this.neueMessstelleId = ergebnis.neueMessstelleId;
        this.snackBar.open('Messstelle dupliziert.', 'OK', { duration: 3000 });

        // Frisch laden – wie nach Anlegen und Löschen: Anzahl, Filter,
        // Fortschritt und ein offline gehaltener Bestand kommen so aus
        // demselben Stand. Die Kopie wird danach angesprungen.
        this.ladeListe(listeId);
      });
  }


  /**
   * Nach dem Nachladen: die Kopie anspringen und kurz hervorheben – oder
   * sagen, warum sie nicht zu sehen ist. Eine unsichtbare Kopie ohne Hinweis
   * wäre für den Benutzer ein Fehlschlag.
   */
  private springeZurNeuenMessstelle(): void {

    const id = this.neueMessstelleId;
    if (id == null) {
      return;
    }
    this.neueMessstelleId = null;

    const index = this.dataSource.filteredData.findIndex(m => m.id === id);

    if (index < 0) {
      const vorhanden = (this.liste?.messstellen ?? []).some(m => m.id === id);
      this.snackBar.open(
        vorhanden
          ? 'Die Kopie ist angelegt, wird aber durch die aktuellen Filter ausgeblendet.'
          : 'Die Kopie ist angelegt.',
        'OK',
        { duration: 6000 }
      );
      return;
    }

    // Auf die Seite blättern, auf der die Kopie steht – Tabelle wie Karten.
    const paginator = this.dataSource.paginator;
    if (paginator && paginator.pageSize > 0) {
      paginator.pageIndex = Math.floor(index / paginator.pageSize);
      this.dataSource.paginator = paginator;
    }

    this.hervorgehobeneMessstelleId = id;
    setTimeout(() => {
      if (this.hervorgehobeneMessstelleId === id) {
        this.hervorgehobeneMessstelleId = null;
      }
    }, 4000);
  }

  messstelleLoeschen(
    messstelle: Messstelle,
    event: Event
  ): void {

    // Der Klick gehört der Schaltfläche, nicht der Zeile bzw. Karte.
    event.stopPropagation();

    if (!this.kannLoeschen || !this.liste || this.loeschtMessstelleId !== null) {
      return;
    }

    const kennzeichnung = this.kennzeichnungWert(messstelle);
    const langtext = this.langtextWert(messstelle);

    const details = [
      kennzeichnung ? `Kennzeichnung: ${kennzeichnung}` : `Zeile ${messstelle.rowNumber}`,
      ...(langtext ? [`Langtext: ${langtext}`] : [])
    ];

    const erledigt = messstelle.pruefpunkteErledigt ?? 0;
    const gesamt = messstelle.pruefpunkteGesamt ?? 0;

    const hatErgebnisse =
      erledigt > 0
      || this.hatPruefergebnis(messstelle)
      || normalizeSignalStatus(messstelle.signalStatus) !== SignalStatus.Offen;

    const warnung = hatErgebnisse
      ? 'Achtung: Für diese Messstelle sind bereits Prüfergebnisse erfasst'
      + (gesamt > 0 ? ` (${erledigt} von ${gesamt} Prüfpunkten bearbeitet)` : '')
      + '. Prüfpunkte, Prüfverlauf, Fotos und Geräte-Dateien werden unwiderruflich mitgelöscht.'
      : 'Prüfpunkte, Prüfverlauf, Fotos und Geräte-Dateien der Messstelle werden mitgelöscht. '
      + 'Das lässt sich nicht rückgängig machen.';

    const data: ConfirmDialogData = {
      title: 'Messstelle wirklich löschen?',
      message: `Die Messstelle wird aus der Inbetriebnahme „${this.liste.name}“ entfernt. `
        + 'Die übrigen Messstellen bleiben unverändert.',
      details,
      warning: warnung,
      confirmText: 'Löschen'
    };

    const listeId = this.liste.id;

    this.dialog
      .open(ConfirmDialogComponent, { data, maxWidth: '520px' })
      .afterClosed()
      .subscribe(bestaetigt => {

        if (!bestaetigt) {
          return;
        }

        this.loeschtMessstelleId = messstelle.id;

        this.service
          .deleteMessstelle(listeId, messstelle.id)
          .subscribe({

            next: () => {
              this.loeschtMessstelleId = null;

              this.snackBar.open('Messstelle gelöscht.', 'OK', { duration: 3000 });

              // Frisch laden: Anzahl, Filter, Anlagenteile, Kategorien und
              // Fortschritt kommen so aus demselben Stand wie die Liste.
              this.ladeListe(listeId);
            },

            error: err => {
              this.loeschtMessstelleId = null;

              this.snackBar.open(
                err?.error?.message
                ?? 'Die Messstelle konnte nicht gelöscht werden.',
                'OK',
                { duration: 5000 }
              );
            }

          });
      });
  }

  // ========================================================
  // INIT
  // ========================================================

  ngOnInit(): void {

    // Meldet sich sofort mit dem aktuellen Zustand und danach bei jedem Wechsel.
    this.breakpointSub =
      this.breakpointObserver
        .observe(MessstellenListeComponent.MOBILE_QUERY)
        .subscribe(state => this.setzeMobil(state.matches));

    this.dataSource.sortingDataAccessor = (messstelle, spalte) => {
      if (spalte === '__row') {
        return messstelle.rowNumber;
      }

      if (spalte === '__anlagenteil') {
        return messstelle.anlagenteilName ?? '';
      }

      if (spalte === '__pruefung') {
        return messstelle.kategorieName ?? '';
      }

      const wert = messstelle.values?.[spalte] ?? '';

      return String(wert).trim().toLowerCase();
    };


    this.dataSource.filterPredicate =
      (
        messstelle: Messstelle,
        filterText: string
      ) => {

        let filter: MessstellenFilter;


        try {

          filter =
            JSON.parse(
              filterText
            );

        } catch {

          filter = {
            search: '',
            kategorie: '',
            status: 'alle',
            signalStatus: '',
            anlagenteil: ''
          };

        }


        // ====================================================
        // SUCHE
        // ====================================================

        const search =
          (
            filter.search
            ?? ''
          )
            .trim()
            .toLowerCase();


        let passtZurSuche =
          true;


        if (search) {

          const excelWerte =
            Object.values(
              messstelle.values ?? {}
            )
              .map(
                value =>
                  String(value ?? '')
              )
              .join(' ')
              .toLowerCase();


          const weitereWerte =
            [
              messstelle.rowNumber,
              messstelle.kategorieName ?? '',
              messstelle.anlagenteilName ?? ''
            ]
              .join(' ')
              .toLowerCase();


          passtZurSuche =
            excelWerte.includes(search)
            ||
            weitereWerte.includes(search);
        }


        // ====================================================
        // KATEGORIE
        // ====================================================

        let passtZurKategorie =
          true;


        if (filter.kategorie) {

          if (
            filter.kategorie === '__keine__'
          ) {

            passtZurKategorie =
              !messstelle.kategorieName;

          } else {

            passtZurKategorie =
              (
                messstelle.kategorieName
                ?? ''
              )
                .trim()
                .toLowerCase()
              ===
              filter.kategorie
                .trim()
                .toLowerCase();

          }
        }


        // ====================================================
        // STATUS
        // ====================================================

        const status =
          this.getStatus(
            messstelle
          );


        const passtZumStatus =
          filter.status === 'alle'
          ||
          filter.status === status;


        // ====================================================
        // SIGNALSTATUS
        // ====================================================

        const passtZumSignalStatus =
          !filter.signalStatus
          ||
          normalizeSignalStatus(messstelle.signalStatus)
          === Number(filter.signalStatus);


        // ====================================================
        // ANLAGENTEIL
        //
        // Wirkt zusätzlich zu allen übrigen Filtern, nicht
        // statt ihnen: "Absackanlage + Kategorie Druck + offen"
        // ist genau der Fall, für den die Gruppierung gedacht ist.
        // ====================================================

        let passtZumAnlagenteil =
          true;


        if (filter.anlagenteil) {

          passtZumAnlagenteil =
            filter.anlagenteil === '__keiner__'
              ? messstelle.anlagenteilId == null
              : messstelle.anlagenteilId
              === Number(filter.anlagenteil);

        }


        return (
          passtZurSuche
          &&
          passtZurKategorie
          &&
          passtZumStatus
          &&
          passtZumSignalStatus
          &&
          passtZumAnlagenteil
        );
      };


    const id =
      Number(
        this.route
          .snapshot
          .paramMap
          .get('id')
      );


    if (!id) {

      this.loadError =
        'Ungültige Listen-Id.';

      this.isLoading =
        false;

      return;
    }


    this.ladeListe(
      id
    );
  }


  ngOnDestroy(): void {

    this.breakpointSub?.unsubscribe();

    if (this.sammelTimer !== null) {
      clearTimeout(this.sammelTimer);
    }
  }


  // ========================================================
  // MOBILE / DESKTOP
  // ========================================================

  /**
   * Wechsel zwischen Karten und Tabelle.
   *
   * Die Seitengröße wird wirklich umgestellt, nicht nur das Aussehen: auf
   * Mobile liefert die Datenquelle dadurch nur fünf Messstellen je Seite.
   * Nach jedem Wechsel geht es auf Seite 1 – die bisherige Seitennummer
   * bezog sich auf eine andere Seitengröße.
   */
  private setzeMobil(
    mobil: boolean
  ): void {

    if (mobil === this.istMobil) {
      return;
    }

    this.istMobil = mobil;


    const paginator = this.paginator;

    if (!paginator) {
      // Noch nicht gerendert – er startet über die Bindung im Template
      // gleich mit der richtigen Seitengröße.
      return;
    }

    paginator.pageIndex = 0;

    paginator.pageSize =
      mobil
        ? this.mobileSeitenGroesse
        : this.desktopSeitenGroesse;

    // Setzen der Werte allein löst kein `page`-Ereignis aus; die erneute
    // Zuweisung lässt die Datenquelle die Seite neu schneiden.
    this.dataSource.paginator = paginator;
  }


  /** Merkt sich eine auf dem Desktop gewählte Seitengröße. */
  onPage(
    event: PageEvent
  ): void {

    if (!this.istMobil) {
      this.desktopSeitenGroesse = event.pageSize;
    }
  }


  /** Kennzeichnung der Messstelle (KKS, AKZ, Messkreisnummer …) für die Karte. */
  kennzeichnungWert(
    messstelle: Messstelle
  ): string {

    return this.zellWert(messstelle, this.kennzeichnungSpalte);
  }


  /** Langtext bzw. Bezeichnung der Messstelle für die Karte. */
  langtextWert(
    messstelle: Messstelle
  ): string {

    return this.zellWert(messstelle, this.langtextSpalte);
  }


  private zellWert(
    messstelle: Messstelle,
    spalte: string | null
  ): string {

    return spalte
      ? String(messstelle.values?.[spalte] ?? '').trim()
      : '';
  }


  // ========================================================
  // LISTE LADEN
  // ========================================================

  private ladeListe(
    id: number
  ): void {

    this.data
      .getListe(id)
      .subscribe({

        next: ergebnis => {

          this.quelle =
            ergebnis.quelle;

          this.standUtc =
            ergebnis.standUtc ?? null;


          this.uebernehmeListe(
            ergebnis.daten
          );


          this.isLoading =
            false;


          /*
           * Die Projektzuordnung braucht den Server. Ohne Verbindung wird sie
           * gar nicht erst angeboten, statt sie mit einer leeren Auswahl
           * anzuzeigen.
           */
          if (
            !ergebnis.daten.projektId
            &&
            ergebnis.quelle === 'server'
            &&
            this.canEdit
          ) {

            this.ladeProjekte();

          }

        },


        error: err => {

          this.loadError =
            err?.message
            ?? 'Die Messstellenliste konnte nicht geladen werden.';

          this.isLoading =
            false;

        }

      });
  }


  // ========================================================
  // LISTE ÜBERNEHMEN
  // ========================================================

  private uebernehmeListe(
    liste: MessstellenListeDetail
  ): void {

    this.liste =
      liste;

    const vorhandeneIds = new Set(
      (liste.messstellen ?? []).map(m => m.id)
    );

    this.ausgewaehlteMessstellen.forEach(id => {
      if (!vorhandeneIds.has(id)) {
        this.ausgewaehlteMessstellen.delete(id);
      }
    });


    const { spalten, feldJeSpalte } =
      this.sortiereSpalten(liste);


    this.anlagenteile =
      liste.anlagenteile
      ?? [];


    this.displayedColumns = [
      '__select',
      '__row',
      '__pruefung',

      /*
       * Der Anlagenteil steht nur dort, wo es ihn gibt. Eine leere
       * Spalte in jeder Liste wäre nur Breite ohne Aussage.
       */
      ...(this.anlagenteile.length > 0 ? ['__anlagenteil'] : []),

      ...spalten
    ];


    this.baueSpaltenKoepfe(
      spalten,
      feldJeSpalte
    );


    this.merkeKartenSpalten(
      spalten,
      feldJeSpalte
    );


    this.dataSource.data =
      liste.messstellen
      ?? [];


    this.buildKategorien();


    // Filteroptionen „Anlagenteil“ aus den Messstellen DIESER Liste.
    this.aktualisiereFilterAnlagenteile();


    /*
     * Falls Filter bereits gesetzt waren,
     * nach einem Reload erneut anwenden.
     */
    this.applyFilter();

    // Kam der Reload von einer Duplizierung: die Kopie anspringen.
    this.springeZurNeuenMessstelle();
  }


  // ========================================================
  // KATEGORIEN FÜR FILTER
  // ========================================================

  private buildKategorien(): void {

    const messstellen =
      this.liste?.messstellen
      ?? [];


    this.kategorien =
      Array.from(
        new Set(
          messstellen
            .map(
              m =>
                m.kategorieName?.trim()
            )
            .filter(
              (
                name
              ): name is string =>
                !!name
            )
        )
      )
        .sort(
          (
            a,
            b
          ) =>
            a.localeCompare(
              b,
              'de'
            )
        );
  }


  // ========================================================
  // SPALTEN SORTIEREN
  // ========================================================

  /**
   * Alle Excel-Spalten, aber die fachlich wichtigen zuerst.
   *
   * Die Strukturerkennung liefert je Standardfeld die zugehörige
   * Excel-Spalte. Deren Reihenfolge im Katalog des Listentyps ist die
   * fachliche Priorität. Danach folgt der Rest in der Reihenfolge der Datei.
   *
   * Nebenbei fällt hier ab, welches Standardfeld hinter einer Spalte steckt.
   * Das ist keine zweite Zuordnungslogik, sondern dasselbe Ergebnis: Die
   * Kopfzeile beschriftet die Spalte damit fachlich („AKS/KKS") statt mit
   * ihrem Excel-Namen („Spalte 10"). An den gespeicherten Daten ändert das
   * nichts – Spaltennamen, Werte und fieldMapping bleiben, wie sie sind.
   */
  private sortiereSpalten(
    liste: MessstellenListeDetail
  ): {
    spalten: string[];
    feldJeSpalte: Map<string, string>;
  } {

    const alle =
      liste.columns
      ?? [];


    const mapping =
      liste.fieldMapping
      ?? {};


    const feldJeSpalte =
      new Map<string, string>();


    if (
      Object.keys(mapping).length === 0
    ) {

      return {
        spalten: [
          ...alle
        ],
        feldJeSpalte
      };

    }


    const vorhanden =
      new Set(
        alle
      );


    const bereitsEinsortiert =
      new Set<string>();


    const wichtige:
      string[] = [];


    for (
      const feld
      of standardFelder(liste.listenTyp)
    ) {

      for (
        const name
        of messstelleFeldMitAliassen(
          feld
        )
      ) {

        const spalte =
          mapping[name];


        if (
          spalte
          &&
          vorhanden.has(spalte)
          &&
          !bereitsEinsortiert.has(spalte)
        ) {

          bereitsEinsortiert.add(
            spalte
          );


          wichtige.push(
            spalte
          );


          // Gemerkt wird der AKTUELLE Feldname, nicht der Alias, unter dem
          // eine ältere Liste ihn gespeichert hat. Sonst stünde über der
          // Spalte „AKS Nummer" statt „AKS/KKS".
          feldJeSpalte.set(
            spalte,
            feld
          );

        }
      }
    }


    return {
      spalten: [
        ...wichtige,

        ...alle.filter(
          spalte =>
            !bereitsEinsortiert.has(
              spalte
            )
        )
      ],
      feldJeSpalte
    };
  }


  // ========================================================
  // SPALTENÜBERSCHRIFTEN
  // ========================================================

  /**
   * Kopfzeilen der Excel-Spalten. Wird beim Übernehmen der Liste einmal
   * gefüllt, damit das Template nur nachschlägt statt bei jedem
   * Änderungslauf zu rechnen.
   */
  private spaltenKoepfe =
    new Map<string, SpaltenKopf>();


  /** Überschrift einer Excel-Spalte. */
  spaltenKopf(
    spalte: string
  ): SpaltenKopf {

    return (
      this.spaltenKoepfe.get(spalte)
      ?? {
        titel: spalte,
        herkunft: null,
        hinweis: ''
      }
    );
  }


  /**
   * Baut die Überschriften auf.
   *
   * Die zweite Zeile entfällt, wenn sie nichts hinzufügt: wenn die
   * Excel-Spalte bereits so heißt wie das Standardfeld („Medium" -> Medium)
   * oder wie einer seiner bekannten früheren Namen („AKS Nummer" ->
   * AKS/KKS). Für diese Gleichheit wird die vorhandene Aliasliste benutzt,
   * keine neue.
   */
  private baueSpaltenKoepfe(
    spalten: string[],
    feldJeSpalte: Map<string, string>
  ): void {

    this.spaltenKoepfe.clear();


    for (
      const spalte
      of spalten
    ) {

      const feld =
        feldJeSpalte.get(spalte);


      if (!feld) {

        // Ohne Zuordnung bleibt alles wie bisher.
        this.spaltenKoepfe.set(
          spalte,
          {
            titel: spalte,
            herkunft: null,
            hinweis: ''
          }
        );

        continue;

      }


      /*
       * Über der Kennzeichnungsspalte steht das tatsächliche System dieser
       * Liste – "KKS", "SKS", "AKZ" –, nicht pauschal "AKS/KKS" wie früher.
       * Ist die Art nicht bekannt, bleibt es beim neutralen "Kennzeichnung".
       */
      const titel =
        feld === FELD_KENNZEICHNUNG
          ? this.kennzeichnungAnzeige
          : feld;


      const sagtDasGleiche =
        [titel, ...messstelleFeldMitAliassen(feld)]
          .some(
            name =>
              this.vergleichbar(name)
              === this.vergleichbar(spalte)
          );


      this.spaltenKoepfe.set(
        spalte,
        {
          titel,

          herkunft: sagtDasGleiche
            ? null
            : spalte,

          hinweis: sagtDasGleiche
            ? ''
            : `Originale Excel-Spalte: ${spalte}`
        }
      );

    }
  }


  /**
   * Welche Spalten die Karte als Kennzeichnung und Langtext zeigt.
   *
   * Dieselbe Zuordnung wie für die Tabellenköpfe (`feldJeSpalte` aus
   * sortiereSpalten) – keine zweite Suche. Ohne Zuordnung steht wie in der
   * Tabelle die erste Spalte vorne.
   */
  private merkeKartenSpalten(
    spalten: string[],
    feldJeSpalte: Map<string, string>
  ): void {

    const spalteVon = (feld: string): string | null =>
      spalten.find(s => feldJeSpalte.get(s) === feld) ?? null;


    this.kennzeichnungSpalte =
      spalteVon(FELD_KENNZEICHNUNG)
      ?? spalten[0]
      ?? null;


    const text =
      spalteVon('Langtext')
      ?? spalteVon('Kurztext');

    this.langtextSpalte =
      text !== this.kennzeichnungSpalte
        ? text
        : null;
  }


  /** Namen vergleichbar machen: Groß-/Kleinschreibung und Leerraum egal. */
  private vergleichbar(
    name: string
  ): string {

    return name
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }


  // ========================================================
  // PROJEKTE LADEN
  // ========================================================

  private ladeProjekte(): void {

    this.loadingProjekte =
      true;


    this.projektAssignmentError =
      '';


    this.projektService
      .getProjekte()
      .subscribe({

        next: projekte => {

          const alle =
            projekte
            ?? [];


          const customerId =
            this.liste
              ?.customerId
            ?? null;


          this.projekte =
            customerId != null
              ? alle.filter(
                p =>
                  p.customerId
                  === customerId
              )
              : alle;


          this.loadingProjekte =
            false;

        },


        error: () => {

          this.projekte =
            [];


          this.loadingProjekte =
            false;


          this.projektAssignmentError =
            'Die Projekte konnten nicht geladen werden.';

        }

      });
  }


  // ========================================================
  // PROJEKT ZUORDNEN
  // ========================================================

  projektZuordnen(): void {

    if (
      !this.liste
      ||
      !this.selectedProjektId
      ||
      this.assigningProjekt
    ) {

      return;
    }


    this.assigningProjekt =
      true;


    this.projektAssignmentError =
      '';


    this.service
      .setProjekt(
        this.liste.id,
        this.selectedProjektId
      )
      .subscribe({

        next: liste => {

          this.assigningProjekt =
            false;


          this.selectedProjektId =
            null;


          this.uebernehmeListe(
            liste
          );


          this.snackBar.open(

            `Inbetriebnahme wurde dem Projekt ${liste.projektNummer
            ??
            liste.projektName
            ??
            ''
            } zugeordnet.`,

            'OK',

            {
              duration: 3000
            }

          );

        },


        error: err => {

          this.assigningProjekt =
            false;


          this.projektAssignmentError =
            err?.error?.message
            ??
            'Das Projekt konnte nicht zugeordnet werden.';

        }

      });
  }


  // ========================================================
  // FILTER
  // ========================================================

  applyFilter(): void {

    const filter: MessstellenFilter = {

      search:
        this.searchTerm
          .trim()
          .toLowerCase(),

      kategorie:
        this.selectedKategorie,

      status:
        this.selectedStatus,

      signalStatus:
        this.selectedSignalStatus,

      anlagenteil:
        this.selectedAnlagenteil
    };


    this.dataSource.filter =
      JSON.stringify(
        filter
      );


    if (
      this.dataSource.paginator
    ) {

      this.dataSource
        .paginator
        .firstPage();

    }
  }


  clearSearch(): void {

    this.searchTerm =
      '';

    this.applyFilter();
  }


  clearAllFilters(): void {

    this.searchTerm =
      '';

    this.selectedKategorie =
      '';

    this.selectedStatus =
      'alle';

    this.selectedSignalStatus =
      '';

    this.selectedAnlagenteil =
      '';


    this.applyFilter();
  }


  get hatAktiveFilter(): boolean {

    return (
      !!this.searchTerm.trim()
      ||
      !!this.selectedKategorie
      ||
      this.selectedStatus
      !== 'alle'
      ||
      !!this.selectedSignalStatus
      ||
      !!this.selectedAnlagenteil
    );
  }


  // ========================================================
  // KENNZEICHNUNG UND LISTENTYP
  // ========================================================

  /**
   * Wie die Kennzeichnung dieser Liste heißt – "KKS", "SKS", "AKZ" … oder
   * neutral "Kennzeichnung", solange die Art nicht feststeht.
   *
   * Der Server liefert die Beschriftung fertig; der lokale Rückfall greift
   * nur bei einem älteren Offline-Bestand, der sie noch nicht kennt.
   */
  get kennzeichnungAnzeige(): string {
    return this.liste?.kennzeichnungLabel
      || kennzeichnungLabel(this.liste?.kennzeichnungsArt);
  }


  /** Standardfelder für das Ergänzen einer Messstelle – abhängig vom Listentyp. */
  get standardFelder(): string[] {
    return standardFelder(this.liste?.listenTyp);
  }


  feldAnzeige(feld: string): string {
    return feld === FELD_KENNZEICHNUNG
      ? this.kennzeichnungAnzeige
      : feld;
  }


  // ========================================================
  // MANUELL ERSTELLTE LISTE (EIGENE SPALTENSTRUKTUR)
  //
  // Eine manuell erstellte Liste trägt ihre Struktur: Spalten mit
  // Datentyp, Pflicht und der Kennzeichnungsspalte. Formular und
  // Prüfung bauen darauf auf; alles Übrige (Prüfung, Fortschritt,
  // Filter, Offline) ist dasselbe wie bei einer importierten Liste.
  // ========================================================

  get struktur(): ListenStruktur | null {
    return this.liste?.struktur ?? null;
  }


  /** Spalten der Struktur in Anzeigereihenfolge; leer bei importierten Listen. */
  get strukturSpalten(): ListenSpalte[] {
    return strukturSpalten(this.liste?.struktur);
  }


  get istManuelleListe(): boolean {
    return this.strukturSpalten.length > 0;
  }


  /** Die Kennzeichnungsspalte der Struktur (z. B. „KKS“, „Gerätenummer“). */
  get strukturKennzeichnung(): ListenSpalte | null {
    return kennzeichnungsSpalte(this.liste?.struktur);
  }


  /** Für die Hervorhebung der Kennzeichnungsspalte in Tabelle und Karten. */
  istKennzeichnungSpalte(spalte: string): boolean {
    return this.kennzeichnungSpalte != null && spalte === this.kennzeichnungSpalte;
  }


  /**
   * Felder für die Regeln „Kategorien/Anlagenteile zuordnen“: bei einer
   * manuellen Liste ihre eigenen Spalten, sonst die Standardfelder.
   */
  get regelFelder(): string[] {
    return this.istManuelleListe
      ? this.strukturSpalten.map(s => s.name)
      : this.standardFelder;
  }


  trackBySpalte(_index: number, spalte: ListenSpalte): string {
    return spalte.id || spalte.name;
  }


  /** Typ- und Pflichtprüfung eines Eingabefelds der neuen Messstelle; leer = in Ordnung. */
  neueFeldFehler(spalte: ListenSpalte): string {

    const roh = this.neueWerte[spalte.id] ?? '';

    // Datumsfelder stehen während der Eingabe im ISO-Format des Browsers.
    const wert = spalte.datentyp === 'Datum' ? isoAlsDatum(roh) : roh;

    const typFehler = pruefeSpaltenWert(spalte, wert);

    if (typFehler) {
      return typFehler;
    }

    if (spalte.istKennzeichnung && wert.trim() && this.kennzeichnungVergeben(wert)) {
      return `„${wert.trim()}“ ist in dieser Liste bereits vergeben.`;
    }

    return '';
  }


  /** Pflichtfeld noch leer – erst nach dem ersten Speicherversuch angezeigt. */
  neuePflichtFehlt(spalte: ListenSpalte): boolean {
    return this.neueGeprueft
      && spalte.pflicht
      && !(this.neueWerte[spalte.id] ?? '').trim();
  }


  /** Gibt es die Kennzeichnung in dieser Liste schon? Gross-/Kleinschreibung und Leerraum egal. */
  private kennzeichnungVergeben(wert: string): boolean {

    const spalte = this.kennzeichnungSpalte;

    if (!spalte) {
      return false;
    }

    const gesucht = this.vergleichbar(wert);

    return (this.liste?.messstellen ?? []).some(m =>
      this.vergleichbar(m.values?.[spalte] ?? '') === gesucht);
  }


  /** Gesammelte Fehler der neuen Messstelle – Grundlage für den Speichern-Knopf. */
  private get neueStrukturFehler(): string[] {

    const fehler: string[] = [];

    for (const spalte of this.strukturSpalten) {

      const wert = (this.neueWerte[spalte.id] ?? '').trim();

      if (spalte.pflicht && !wert) {
        fehler.push(spalte.istKennzeichnung
          ? `Die Kennzeichnung „${spalte.name}“ ist ein Pflichtfeld.`
          : `„${spalte.name}“ ist ein Pflichtfeld.`);
        continue;
      }

      const feldFehler = this.neueFeldFehler(spalte);

      if (feldFehler) {
        fehler.push(feldFehler);
      }
    }

    return fehler;
  }


  /** Struktur der Liste ändern – Spalten ergänzen, umbenennen, sortieren, Kennzeichnung wechseln. */
  strukturBearbeiten(): void {

    if (!this.liste || !this.istManuelleListe || !this.canEdit || this.istOffline) {
      return;
    }

    const data: StrukturBearbeitenDialogData = { liste: this.liste };

    this.dialog
      .open(StrukturBearbeitenDialogComponent, { data, maxWidth: '95vw', autoFocus: false })
      .afterClosed()
      .subscribe(aktualisiert => {

        if (!aktualisiert) {
          return;
        }

        this.snackBar.open('Spaltenstruktur gespeichert.', 'OK', { duration: 3000 });

        // Frisch laden: Spaltennamen und Werte können sich geändert haben.
        this.ladeListe(aktualisiert.id);
      });
  }


  /** Struktur dieser Liste als Vorlage für weitere Listen ablegen. */
  alsVorlageSpeichern(): void {

    if (!this.liste || !this.istManuelleListe || !this.canEdit || this.istOffline) {
      return;
    }

    const liste = this.liste;

    const data: AlsVorlageDialogData = {
      listeName: liste.name,
      spaltenCount: this.strukturSpalten.length,
      kennzeichnung: this.strukturKennzeichnung?.name ?? this.kennzeichnungAnzeige
    };

    this.dialog
      .open(AlsVorlageDialogComponent, { data, maxWidth: '95vw' })
      .afterClosed()
      .subscribe((ergebnis: AlsVorlageDialogErgebnis | null | undefined) => {

        if (!ergebnis) {
          return;
        }

        this.service.listeAlsVorlage(liste.id, ergebnis.name, ergebnis.beschreibung).subscribe({
          next: vorlage => {
            this.snackBar.open(`Vorlage „${vorlage.name}“ gespeichert.`, 'OK', { duration: 3500 });
          },
          error: err => {
            this.snackBar.open(
              err?.error?.message ?? 'Die Vorlage konnte nicht gespeichert werden.',
              'OK',
              { duration: 4500 });
          }
        });
      });
  }


  /**
   * Die Excel-Rohwerte hinter einem Anlagenteil ("UKR12"), sofern sein Name
   * ein Klartext ist. Bei gleichem Text wäre die Anzeige nur doppelt.
   */
  anlagenteilHerkunft(anlagenteil: Anlagenteil): string {
    const quellen = (anlagenteil.quellwerte ?? [])
      .filter(q => (q ?? '').trim().length > 0
        && q.trim().toLowerCase() !== anlagenteil.name.trim().toLowerCase());

    return quellen.join(', ');
  }


  anlagenteilName(messstelle: Messstelle): string {
    if (messstelle.anlagenteilName) {
      return messstelle.anlagenteilName;
    }

    if (messstelle.anlagenteilFremd && messstelle.anlagenteilId != null) {
      return 'Anderes Projekt';
    }

    return this.anlagenteile
      .find(a => a.id === messstelle.anlagenteilId)?.name
      ?? '';
  }

  /** Verweis auf einen Anlagenteil, der nicht zum Projekt dieser Liste gehört. */
  anlagenteilFremd(messstelle: Messstelle): boolean {
    return !!messstelle.anlagenteilFremd && messstelle.anlagenteilId != null;
  }


  // ========================================================
  // MESSSTELLE ERGÄNZEN
  // ========================================================

  neueMessstelleOeffnen(): void {

    this.zeigeNeueMessstelle = true;
    this.neueFehler = '';
    this.neueGeprueft = false;
    this.neueWerte = {};
    this.neueKategorieId = null;
    this.neueAnlagenteilId = null;

    this.ladeKategorienAuswahl();
  }


  /** Die Kategorien zur Auswahl – einmal geladen, dann wiederverwendet. */
  private ladeKategorienAuswahl(): void {
    if (this.kategorienAuswahl.length === 0) {
      this.service.getKategorien().subscribe({
        next: kategorien => this.kategorienAuswahl = kategorien,
        error: () => this.kategorienAuswahl = []
      });
    }
  }


  neueMessstelleAbbrechen(): void {
    this.zeigeNeueMessstelle = false;
    this.neueFehler = '';
  }


  get kannNeueSpeichern(): boolean {

    if (this.speichertNeue) {
      return false;
    }

    if (this.istManuelleListe) {
      // Typfehler und doppelte Kennzeichnung sperren sofort; leere
      // Pflichtfelder meldet erst der Speicherversuch – sonst wäre der
      // Knopf beim Öffnen des Formulars grundlos grau.
      return this.strukturSpalten.every(s => !this.neueFeldFehler(s))
        && Object.values(this.neueWerte).some(v => (v ?? '').trim().length > 0);
    }

    return Object.values(this.neueWerte).some(v => (v ?? '').trim().length > 0);
  }


  neueMessstelleSpeichern(): void {

    if (!this.liste || !this.kannNeueSpeichern) {
      return;
    }

    let werte: { [feld: string]: string } = this.neueWerte;

    if (this.istManuelleListe) {

      this.neueGeprueft = true;

      const fehler = this.neueStrukturFehler;

      if (fehler.length > 0) {
        this.neueFehler = fehler.join(' ');
        return;
      }

      /*
       * Schlüssel ist die stabile Spalten-Id – der Server löst sie auf die
       * Spalte auf, auch wenn jemand sie zwischenzeitlich umbenannt hat.
       * Datumsfelder gehen in der gespeicherten Form TT.MM.JJJJ hinaus.
       */
      werte = {};

      for (const spalte of this.strukturSpalten) {
        const roh = (this.neueWerte[spalte.id] ?? '').trim();

        if (!roh) {
          continue;
        }

        werte[spalte.id] = spalte.datentyp === 'Datum' ? isoAlsDatum(roh) : roh;
      }
    }

    this.speichertNeue = true;
    this.neueFehler = '';

    this.service.createMessstelle(
      this.liste.id,
      werte,
      this.neueKategorieId,
      this.neueAnlagenteilId
    ).subscribe({
      next: messstelle => {
        this.speichertNeue = false;
        this.zeigeNeueMessstelle = false;

        this.snackBar.open('Messstelle ergänzt.', 'OK', { duration: 3000 });

        // Frisch laden: die Liste kann eine neue Spalte bekommen haben.
        this.ladeListe(messstelle.messstellenListeId);
      },
      error: err => {
        this.speichertNeue = false;
        this.neueFehler =
          err?.error?.message ?? 'Die Messstelle konnte nicht angelegt werden.';
      }
    });
  }


  // ========================================================
  // ABGLEICH MIT EINER NEUEREN EXCEL-DATEI
  // ========================================================

  abgleichOeffnen(): void {
    this.zeigeAbgleich = true;
    this.abgleich = null;
    this.abgleichDatei = null;
    this.abgleichFehler = '';
    this.abgleichBearbeiten = false;
    this.auswahlLeeren();
  }


  private auswahlLeeren(): void {
    this.gewaehlteNeue.clear();
    this.gewaehlteFelder.clear();
    this.gewaehlteStruktur.clear();
    this.gewaehlteEntfallene.clear();
  }


  // ── Entfallene Messstellen löschen ──
  //
  // Bewusst getrennt von „alle auswählen“: eine Messstelle mit Prüfpunkten,
  // Protokoll und Fotos verschwindet nur, wenn sie einzeln angehakt wurde.

  /** Ids entfallener Messstellen, die auf ausdrücklichen Wunsch gelöscht werden. */
  gewaehlteEntfallene = new Set<number>();

  istEntfallenGewaehlt(eintrag: AbgleichEintrag): boolean {
    return eintrag.messstelleId != null && this.gewaehlteEntfallene.has(eintrag.messstelleId);
  }

  toggleEntfallen(eintrag: AbgleichEintrag): void {
    if (eintrag.art !== 'Entfallen' || eintrag.messstelleId == null) {
      return;
    }

    if (this.gewaehlteEntfallene.has(eintrag.messstelleId)) {
      this.gewaehlteEntfallene.delete(eintrag.messstelleId);
    } else {
      this.gewaehlteEntfallene.add(eintrag.messstelleId);
    }
  }

  get entfalleneZumLoeschen(): number {
    return this.gewaehlteEntfallene.size;
  }


  abgleichSchliessen(): void {
    this.zeigeAbgleich = false;
  }


  onAbgleichDatei(event: Event): void {

    const input = event.target as HTMLInputElement;
    const datei = input.files && input.files.length > 0 ? input.files[0] : null;

    input.value = '';

    if (!datei || !this.liste) {
      return;
    }

    this.abgleichDatei = datei;
    this.vergleichsSpalte = null;
    this.zeigeUnveraenderte = false;
    this.abgleichBearbeiten = false;

    this.ladeAbgleich(datei);
  }


  /**
   * Führt den Vergleich aus – beim Auswählen der Datei und erneut, wenn der
   * Benutzer eine Vergleichsspalte gewählt hat.
   */
  private ladeAbgleich(datei: File): void {

    if (!this.liste) {
      return;
    }

    this.abgleichLaeuft = true;
    this.abgleichFehler = '';
    this.abgleich = null;

    this.service.abgleich(this.liste.id, datei, this.vergleichsSpalte).subscribe({
      next: ergebnis => {
        this.abgleichLaeuft = false;
        this.abgleich = ergebnis;

        this.alleAuswaehlen();
      },
      error: err => {
        this.abgleichLaeuft = false;
        this.abgleichFehler =
          err?.error?.message ?? 'Der Abgleich ist fehlgeschlagen.';
      }
    });
  }


  // ── Gruppen des Änderungsberichts ──
  //
  // Nach Dringlichkeit sortiert: was zu entscheiden ist, steht oben.
  // „Unverändert“ und die nicht als Messstelle erkannten Zeilen sind der
  // Regelfall und deshalb eingeklappt – sie sind die Bestätigung, dass nichts
  // zu tun ist, und keine Arbeitsliste.

  private eintraegeDerArt(art: string): AbgleichEintrag[] {
    return (this.abgleich?.eintraege ?? []).filter(e => e.art === art);
  }

  get abgleichGeaendert(): AbgleichEintrag[] { return this.eintraegeDerArt('Geaendert'); }
  get abgleichNeu(): AbgleichEintrag[] { return this.eintraegeDerArt('Neu'); }
  get abgleichEntfallen(): AbgleichEintrag[] { return this.eintraegeDerArt('Entfallen'); }
  get abgleichUnsicher(): AbgleichEintrag[] { return this.eintraegeDerArt('Unsicher'); }

  /**
   * Zeilen, die schon beim letzten Import bewusst weggelassen wurden und
   * seitdem unverändert sind. Sie stehen bewusst NICHT unter 'neu' - wer sie
   * doch haben will, hakt sie im Bearbeitungsmodus an.
   */
  get abgleichAusgeschlossen(): AbgleichEintrag[] { return this.eintraegeDerArt('Ausgeschlossen'); }
  get abgleichUnveraendert(): AbgleichEintrag[] { return this.eintraegeDerArt('Unveraendert'); }

  /**
   * Gruppenköpfe, Summen-, Zubehör- und Hilfszeilen. Keine Messstellen, also
   * auch keine Änderungen – sie stehen nur da, damit niemand sie vermisst.
   */
  get abgleichOhneKennzeichnung(): AbgleichEintrag[] {
    return this.eintraegeDerArt('OhneKennzeichnung');
  }

  /** Die Strukturänderungen – neue, entfallene und umbenannte Spalten. */
  get abgleichStruktur(): StrukturAenderung[] {
    return this.abgleich?.strukturaenderungen ?? [];
  }

  get abgleichNeueSpalten(): StrukturAenderung[] {
    return this.abgleichStruktur.filter(s => s.art === 'NeueSpalte');
  }

  get abgleichEntfalleneSpalten(): StrukturAenderung[] {
    return this.abgleichStruktur.filter(s => s.art === 'EntfalleneSpalte');
  }

  get abgleichUmbenannteSpalten(): StrukturAenderung[] {
    return this.abgleichStruktur.filter(s => s.art === 'SpalteUmbenannt');
  }

  /** „Unverändert“ ist standardmäßig zugeklappt. */
  zeigeUnveraenderte = false;

  /** Die nicht als Messstelle erkannten Zeilen ebenso. */
  zeigeOhneKennzeichnung = false;

  strukturText(s: StrukturAenderung): string {
    switch (s.art) {
      case 'NeueSpalte':
        return `Neue Spalte „${s.spalte}“`;
      case 'EntfalleneSpalte':
        return `Spalte „${s.spalte}“ nicht mehr in der Datei`;
      default:
        return `Spalte „${s.bisherigeSpalte}“ heißt jetzt „${s.spalte}“`;
    }
  }


  strukturHinweis(s: StrukturAenderung): string {
    switch (s.art) {
      case 'NeueSpalte':
        return s.betroffeneZeilen === 0
          ? 'Keine Zeile der Datei liefert dazu einen Wert.'
          : `${s.betroffeneZeilen} Zeilen liefern dazu einen Wert. `
          + 'Beim Übernehmen entsteht die Spalte einmal für die ganze Liste.';
      case 'EntfalleneSpalte':
        return s.betroffeneZeilen === 0
          ? 'In der Liste steht dazu nichts – sie kann bedenkenlos weg.'
          : `${s.betroffeneZeilen} Messstellen haben hier einen Wert. `
          + 'Beim Übernehmen verschwindet nur die Spalte; die Werte bleiben erhalten.';
      default:
        return 'Bereits über ihre Bedeutung zugeordnet – es gibt nichts zu übernehmen.';
    }
  }


  // ── Vergleichsspalte ──
  //
  // Nur nötig, wenn der Server keine Kennzeichnung bestimmen konnte. Er rät
  // dann bewusst nicht über Zeilennummern, sondern fragt.

  vergleichsSpalte: string | null = null;

  get brauchtVergleichsSpalte(): boolean {
    return !!this.abgleich?.matchingUnsicher;
  }

  get vergleichsSpaltenVorschlaege(): string[] {
    return this.abgleich?.vergleichsSpaltenVorschlaege ?? [];
  }

  /** Erneut vergleichen, diesmal über die gewählte Spalte. */
  vergleichsSpalteAnwenden(spalte: string): void {
    if (!this.abgleichDatei) {
      return;
    }

    this.vergleichsSpalte = spalte;
    this.auswahlLeeren();

    this.ladeAbgleich(this.abgleichDatei);
  }


  /** Mehrfach vergebene Kennzeichnungen – der Benutzer muss sie kennen. */
  get mehrfachVergeben(): string[] {
    return this.abgleich?.mehrfachVergeben ?? [];
  }


  /**
   * Die neue Datei ist inhaltlich deckungsgleich.
   *
   * Der häufigste Fall beim Prüfen einer Revision – und der, bei dem man
   * gerade KEINE Liste vermeintlicher Änderungen sehen will.
   */
  get keineUnterschiede(): boolean {
    return !!this.abgleich?.ohneUnterschied;
  }


  // ── Auswahl ──
  //
  // Drei getrennte Ebenen, weil sie getrennt entschieden werden: ganze neue
  // Messstellen, einzelne Feldänderungen, und der Aufbau der Datei.

  private feldSchluessel(messstelleId: number, spalte: string): string {
    return `${messstelleId}::${spalte}`;
  }

  private strukturSchluessel(s: StrukturAenderung): string {
    return `${s.art}::${s.spalte}`;
  }


  /** Wie viele Änderungen aktuell zur Übernahme angehakt sind (inkl. einzeln gewählter Löschungen). */
  get gewaehlteAnzahl(): number {
    return this.gewaehlteNeue.size
      + this.gewaehlteFelder.size
      + this.gewaehlteStruktur.size
      + this.gewaehlteEntfallene.size;
  }


  /** Wie viele Änderungen der Bericht überhaupt zur Übernahme anbietet. */
  get entscheidbareAnzahl(): number {
    const felder = this.abgleichGeaendert
      .reduce((summe, e) => summe + e.unterschiede.length, 0);

    const struktur = this.abgleichStruktur.filter(s => s.uebernehmbar).length;

    return this.abgleichNeu.length + felder + struktur;
  }


  /**
   * Alles anhaken, was der Bericht zeigt – der Normalfall.
   *
   * Früher ausgeschlossene Zeilen bleiben aussen vor. Für sie liegt schon
   * eine Entscheidung vor; sie hier stillschweigend zurückzunehmen wäre
   * genau das Gegenteil dessen, wofür sie gespeichert wurde.
   */
  alleAuswaehlen(): void {
    this.auswahlLeeren();

    for (const eintrag of this.abgleichNeu) {
      if (eintrag.neueZeile != null) {
        this.gewaehlteNeue.add(eintrag.neueZeile);
      }
    }

    for (const eintrag of this.abgleichGeaendert) {
      if (eintrag.messstelleId == null) {
        continue;
      }

      for (const feld of eintrag.unterschiede) {
        this.gewaehlteFelder.add(this.feldSchluessel(eintrag.messstelleId, feld.spalte));
      }
    }

    for (const s of this.abgleichStruktur) {
      if (s.uebernehmbar) {
        this.gewaehlteStruktur.add(this.strukturSchluessel(s));
      }
    }
  }


  keineAuswaehlen(): void {
    this.auswahlLeeren();
  }


  /** In den Bearbeitungsmodus – ab hier erscheinen die Kästchen. */
  auswahlBearbeiten(): void {
    this.abgleichBearbeiten = true;
  }


  /**
   * Änderungen verwerfen: das Ergebnis wird weggeworfen, an der Liste
   * ändert sich nichts. Es war ohnehin nur ein Bericht.
   */
  abgleichVerwerfen(): void {
    this.abgleich = null;
    this.abgleichDatei = null;
    this.abgleichFehler = '';
    this.vergleichsSpalte = null;
    this.zeigeUnveraenderte = false;
    this.zeigeOhneKennzeichnung = false;
    this.abgleichBearbeiten = false;
    this.auswahlLeeren();
  }


  // ── Einzelne neue Messstelle ──

  /** Ausgeschlossene Zeilen werden wie neue behandelt: über ihre Zeilennummer. */
  private istWieNeu(eintrag: AbgleichEintrag): boolean {
    return eintrag.art === 'Neu' || eintrag.art === 'Ausgeschlossen';
  }


  istZeileGewaehlt(eintrag: AbgleichEintrag): boolean {
    return eintrag.neueZeile != null && this.gewaehlteNeue.has(eintrag.neueZeile);
  }


  toggleZeile(eintrag: AbgleichEintrag): void {
    if (!this.istWieNeu(eintrag) || eintrag.neueZeile == null) {
      return;
    }

    if (this.gewaehlteNeue.has(eintrag.neueZeile)) {
      this.gewaehlteNeue.delete(eintrag.neueZeile);
    } else {
      this.gewaehlteNeue.add(eintrag.neueZeile);
    }
  }


  // ── Einzelne Feldänderung ──

  istFeldGewaehlt(eintrag: AbgleichEintrag, spalte: string): boolean {
    return eintrag.messstelleId != null
      && this.gewaehlteFelder.has(this.feldSchluessel(eintrag.messstelleId, spalte));
  }


  toggleFeld(eintrag: AbgleichEintrag, spalte: string): void {
    if (eintrag.messstelleId == null) {
      return;
    }

    const schluessel = this.feldSchluessel(eintrag.messstelleId, spalte);

    if (this.gewaehlteFelder.has(schluessel)) {
      this.gewaehlteFelder.delete(schluessel);
    } else {
      this.gewaehlteFelder.add(schluessel);
    }
  }


  /** Alle Felder einer Messstelle auf einmal – der übliche Griff. */
  toggleMessstelle(eintrag: AbgleichEintrag): void {
    const anhaken = !this.istMessstelleGanzGewaehlt(eintrag);

    for (const feld of eintrag.unterschiede) {
      if (this.istFeldGewaehlt(eintrag, feld.spalte) !== anhaken) {
        this.toggleFeld(eintrag, feld.spalte);
      }
    }
  }


  istMessstelleGanzGewaehlt(eintrag: AbgleichEintrag): boolean {
    return eintrag.unterschiede.length > 0
      && eintrag.unterschiede.every(f => this.istFeldGewaehlt(eintrag, f.spalte));
  }


  istMessstelleTeilweiseGewaehlt(eintrag: AbgleichEintrag): boolean {
    return !this.istMessstelleGanzGewaehlt(eintrag)
      && eintrag.unterschiede.some(f => this.istFeldGewaehlt(eintrag, f.spalte));
  }


  // ── Strukturänderung ──

  istStrukturGewaehlt(s: StrukturAenderung): boolean {
    return this.gewaehlteStruktur.has(this.strukturSchluessel(s));
  }


  toggleStruktur(s: StrukturAenderung): void {
    if (!s.uebernehmbar) {
      return;
    }

    const schluessel = this.strukturSchluessel(s);

    if (this.gewaehlteStruktur.has(schluessel)) {
      this.gewaehlteStruktur.delete(schluessel);
    } else {
      this.gewaehlteStruktur.add(schluessel);
    }
  }


  abgleichArtText(art: string): string {
    switch (art) {
      case 'Neu': return 'neu';
      case 'Geaendert': return 'geändert';
      case 'Entfallen': return 'nicht mehr in der Datei';
      case 'Unsicher': return 'unsicher – bitte prüfen';
      case 'Ausgeschlossen': return 'beim Import ausgeschlossen';
      case 'OhneKennzeichnung': return 'keine Messstelle';
      default: return 'unverändert';
    }
  }


  get kannAbgleichUebernehmen(): boolean {
    return !this.abgleichLaeuft
      && !!this.abgleichDatei
      && this.gewaehlteAnzahl > 0;
  }


  /**
   * Baut aus den drei Auswahlmengen den Auftrag an den Server.
   *
   * Die Feldänderungen gehen IMMER ausdrücklich mit, auch wenn nichts
   * abgewählt wurde: dann übernimmt der Server genau das, was im Bericht
   * stand, und nicht das, was er beim zweiten Rechnen für richtig hält.
   */
  private abgleichAuswahl(): AbgleichAuswahl {

    const aktualisierte: number[] = [];
    const feldaenderungen: { messstelleId: number; spalten: string[] }[] = [];

    for (const eintrag of this.abgleichGeaendert) {
      if (eintrag.messstelleId == null) {
        continue;
      }

      const spalten = eintrag.unterschiede
        .map(f => f.spalte)
        .filter(s => this.istFeldGewaehlt(eintrag, s));

      if (spalten.length === 0) {
        continue;
      }

      aktualisierte.push(eintrag.messstelleId);
      feldaenderungen.push({ messstelleId: eintrag.messstelleId, spalten });
    }

    return {
      neueZeilen: Array.from(this.gewaehlteNeue),
      aktualisierteMessstellen: aktualisierte,
      feldaenderungen,
      neueSpalten: this.abgleichNeueSpalten
        .filter(s => this.istStrukturGewaehlt(s))
        .map(s => s.spalte),
      entfalleneSpalten: this.abgleichEntfalleneSpalten
        .filter(s => this.istStrukturGewaehlt(s))
        .map(s => s.spalte),
      // Nur einzeln angehakte entfallene Messstellen – nie stillschweigend.
      entfalleneMessstellen: Array.from(this.gewaehlteEntfallene)
    };
  }


  abgleichUebernehmen(): void {

    if (!this.liste || !this.abgleichDatei || !this.kannAbgleichUebernehmen) {
      return;
    }

    this.abgleichLaeuft = true;
    this.abgleichFehler = '';

    this.service.abgleichUebernehmen(
      this.liste.id,
      this.abgleichDatei,
      this.abgleichAuswahl(),
      // Dieselbe Spalte wie im Bericht – sonst übernähme der Server einen
      // anderen Vergleich als den, den der Benutzer gesehen hat.
      this.vergleichsSpalte
    ).subscribe({
      next: liste => {
        this.abgleichLaeuft = false;
        this.zeigeAbgleich = false;
        this.abgleich = null;
        this.abgleichDatei = null;
        this.abgleichBearbeiten = false;

        const geloescht = this.gewaehlteEntfallene.size;
        this.uebernehmeListe(liste);
        this.auswahlLeeren();

        this.snackBar.open(
          geloescht > 0
            ? `Der Abgleich wurde übernommen; ${geloescht} entfallene Messstelle${geloescht === 1 ? '' : 'n'} gelöscht.`
            : 'Der Abgleich wurde übernommen.',
          'OK', { duration: 4000 });
      },
      error: err => {
        this.abgleichLaeuft = false;
        this.abgleichFehler =
          err?.error?.message ?? 'Die Übernahme ist fehlgeschlagen.';
      }
    });
  }


  // ========================================================
  // KATEGORIEN / ANLAGENTEILE PER REGEL ZUORDNEN
  //
  // Dieselben Regeln wie beim Import, nachträglich auf die
  // bestehende Liste angewendet. Gerechnet wird auf dem Server
  // mit derselben Auswertung wie beim Import; hier entstehen nur
  // die Regeln, die Vorschau und die Bestätigung. Online-only.
  // ========================================================

  private static readonly SAMMEL_VORSCHAU_VERZOEGERUNG_MS = 400;

  readonly operatoren = REGEL_OPERATOREN;

  /** Was gerade per Regel vergeben wird. null = Bereich geschlossen. */
  sammelArt: SammelZuordnungArt | null = null;

  sammelRegeln: SammelRegelentwurf[] = [];

  sammelVorschau: SammelZuordnungErgebnis | null = null;

  sammelVorschauLaeuft = false;

  /** Regeln wurden geändert, die Vorschau passt noch nicht dazu. */
  sammelVorschauVeraltet = false;

  sammelSpeichert = false;

  sammelFehler = '';

  private sammelTimer: ReturnType<typeof setTimeout> | null = null;

  /** Zählt Vorschauläufe – eine überholte Antwort wird verworfen. */
  private sammelLauf = 0;


  get kannSammelZuordnen(): boolean {
    return this.canEdit && !this.istOffline;
  }


  sammelOeffnen(art: SammelZuordnungArt): void {

    if (!this.kannSammelZuordnen) {
      return;
    }

    this.sammelAbbrechenIntern();

    this.sammelArt = art;

    this.sammelRegelHinzufuegen();

    if (art === 'kategorie') {
      this.ladeKategorienAuswahl();
    }
  }


  sammelSchliessen(): void {
    this.sammelAbbrechenIntern();
    this.sammelArt = null;
  }


  private sammelAbbrechenIntern(): void {

    if (this.sammelTimer !== null) {
      clearTimeout(this.sammelTimer);
      this.sammelTimer = null;
    }

    this.sammelLauf++;
    this.sammelRegeln = [];
    this.sammelVorschau = null;
    this.sammelVorschauLaeuft = false;
    this.sammelVorschauVeraltet = false;
    this.sammelFehler = '';
  }


  sammelRegelHinzufuegen(): void {

    const kategorie = this.sammelArt === 'kategorie';

    this.sammelRegeln.push({
      // Dieselben Vorgaben wie im Importdialog.
      feld: this.vorhandenesFeld(kategorie ? 'Langtext' : FELD_KENNZEICHNUNG),
      operator: kategorie ? 'enthaelt' : 'beginntMit',
      wert: '',
      kategorieId: null,
      anlagenteilName: '',
      ergebnis: null,
      zeigeBeispiele: false
    });
  }


  sammelRegelEntfernen(index: number): void {
    this.sammelRegeln.splice(index, 1);
    this.sammelAktualisieren();
  }


  /**
   * Das gewünschte Feld, sofern die Liste es kennt – sonst das erste. Bei
   * einer manuellen Liste sind das ihre eigenen Spalten; die Kennzeichnung
   * steht dort vorne, „Langtext“ gibt es nur, wenn so eine Spalte heißt.
   */
  private vorhandenesFeld(feld: string): string {
    const felder = this.regelFelder;

    if (this.istManuelleListe && feld === FELD_KENNZEICHNUNG) {
      return this.strukturKennzeichnung?.name ?? felder[0] ?? feld;
    }

    return felder.includes(feld)
      ? feld
      : felder[0] ?? feld;
  }


  private istSammelRegelVollstaendig(regel: SammelRegelentwurf): boolean {

    if (!regel.feld || !regel.wert.trim()) {
      return false;
    }

    return this.sammelArt === 'kategorie'
      ? regel.kategorieId != null
      : regel.anlagenteilName.trim().length > 0;
  }


  /** Nur vollständige Regeln gehen an den Server. */
  get sammelRegelnVollstaendig(): SammelRegelentwurf[] {
    return this.sammelRegeln.filter(r => this.istSammelRegelVollstaendig(r));
  }


  /** Nach jeder Änderung an den Regeln: Vorschau verzögert neu rechnen. */
  sammelAktualisieren(): void {

    this.sammelVorschauVeraltet = true;
    this.sammelFehler = '';

    // Eine noch laufende Antwort passt nicht mehr zu den Regeln.
    this.sammelLauf++;

    if (this.sammelTimer !== null) {
      clearTimeout(this.sammelTimer);
    }

    this.sammelTimer = setTimeout(
      () => this.ladeSammelVorschau(),
      MessstellenListeComponent.SAMMEL_VORSCHAU_VERZOEGERUNG_MS
    );
  }


  private ladeSammelVorschau(): void {

    this.sammelTimer = null;

    if (!this.liste || !this.sammelArt || !this.kannSammelZuordnen) {
      return;
    }

    const regeln = this.sammelRegelnVollstaendig;
    const art = this.sammelArt;
    const lauf = ++this.sammelLauf;

    for (const regel of this.sammelRegeln) {
      if (!regeln.includes(regel)) {
        regel.ergebnis = null;
      }
    }

    if (regeln.length === 0) {
      this.sammelVorschau = null;
      this.sammelVorschauLaeuft = false;
      this.sammelVorschauVeraltet = false;
      return;
    }

    this.sammelVorschauLaeuft = true;

    this.service
      .sammelZuordnung(this.liste.id, this.baueSammelRegeln(regeln), true)
      .subscribe({

        next: ergebnis => {
          if (lauf !== this.sammelLauf) {
            return;
          }

          this.sammelVorschau = ergebnis;
          this.sammelVorschauLaeuft = false;
          this.sammelVorschauVeraltet = false;

          const jeRegel = art === 'kategorie'
            ? ergebnis.kategorieRegeln
            : ergebnis.anlagenteilRegeln;

          regeln.forEach((regel, i) => regel.ergebnis = jeRegel[i] ?? null);
        },

        error: err => {
          if (lauf !== this.sammelLauf) {
            return;
          }

          this.sammelVorschau = null;
          this.sammelVorschauLaeuft = false;
          this.sammelVorschauVeraltet = false;
          this.sammelFehler =
            err?.error?.message ?? 'Die Vorschau konnte nicht berechnet werden.';
        }

      });
  }


  private baueSammelRegeln(regeln: SammelRegelentwurf[]): SammelZuordnungRegeln {

    if (this.sammelArt === 'kategorie') {
      return {
        kategorien: regeln.map(r => ({
          feld: r.feld,
          operator: r.operator,
          wert: r.wert.trim(),
          kategorieId: r.kategorieId
        })),
        anlagenteile: []
      };
    }

    return {
      kategorien: [],
      anlagenteile: regeln.map(r => ({
        feld: r.feld,
        operator: r.operator,
        wert: r.wert.trim(),
        anlagenteilName: r.anlagenteilName.trim()
      }))
    };
  }


  /** Vorhandene Anlagenteile passend zur Eingabe – neue Namen bleiben erlaubt. */
  anlagenteilVorschlaege(regel: SammelRegelentwurf): Anlagenteil[] {

    const suche = regel.anlagenteilName.trim().toLowerCase();

    return suche
      ? this.anlagenteile.filter(a => a.name.toLowerCase().includes(suche))
      : this.anlagenteile;
  }


  istNeuerAnlagenteil(regel: SammelRegelentwurf): boolean {

    const name = regel.anlagenteilName.trim().toLowerCase();

    return name.length > 0
      && !this.anlagenteile.some(a => a.name.trim().toLowerCase() === name);
  }


  /** „Langtext enthält „Pumpe“ → Pumpen“ – für die Bestätigung. */
  private sammelRegelText(regel: SammelRegelentwurf): string {

    const operator =
      REGEL_OPERATOREN.find(o => o.wert === regel.operator)?.text
      ?? regel.operator;

    const ziel = this.sammelArt === 'kategorie'
      ? this.kategorienAuswahl.find(k => k.id === regel.kategorieId)?.name ?? ''
      : regel.anlagenteilName.trim();

    return `${this.feldAnzeige(regel.feld)} ${operator} „${regel.wert.trim()}“ → ${ziel}`;
  }


  get kannSammelAnwenden(): boolean {
    return this.kannSammelZuordnen
      && !!this.liste
      && !!this.sammelVorschau
      && this.sammelVorschau.betroffen > 0
      && !this.sammelVorschauLaeuft
      && !this.sammelVorschauVeraltet
      && !this.sammelSpeichert;
  }


  sammelAnwenden(): void {

    if (!this.kannSammelAnwenden || !this.liste || !this.sammelVorschau || !this.sammelArt) {
      return;
    }

    const liste = this.liste;
    const vorschau = this.sammelVorschau;
    const kategorie = this.sammelArt === 'kategorie';
    const regeln = this.sammelRegelnVollstaendig;
    const anzahl = vorschau.betroffen;

    const details = regeln.map(r => {
      const aenderungen = r.ergebnis?.aenderungen ?? 0;
      return `${this.sammelRegelText(r)}: ${aenderungen} Änderung${aenderungen === 1 ? '' : 'en'}`;
    });

    if (vorschau.neueAnlagenteile.length > 0) {
      details.push(`Neu angelegt: ${vorschau.neueAnlagenteile.join(', ')}`);
    }

    const zuordnung = kategorie ? 'Kategorie' : 'Anlagenteil';

    let warnung = vorschau.ueberschrieben > 0
      ? `${vorschau.ueberschrieben} dieser Messstellen ${vorschau.ueberschrieben === 1 ? 'hat' : 'haben'} bereits `
      + `${kategorie ? 'eine andere Kategorie' : 'einen anderen Anlagenteil'} – diese Zuordnung wird überschrieben.`
      : `Vorhandene ${zuordnung}-Zuordnungen werden dabei überschrieben – laut Vorschau betrifft das keine Messstelle.`;

    if (kategorie) {
      warnung += ' Unbearbeitete Prüfpunkte der bisherigen Kategorie werden durch die der neuen ersetzt; '
        + 'bereits bearbeitete Prüfpunkte bleiben erhalten.';
    }

    const data: ConfirmDialogData = {
      title: `${anzahl} Messstelle${anzahl === 1 ? ' wird' : 'n werden'} geändert`,
      message: `Die ${zuordnung}-Regeln werden auf die Messstellen der Inbetriebnahme „${liste.name}“ angewendet. `
        + 'Andere Inbetriebnahmen bleiben unverändert.',
      details,
      warning: warnung,
      confirmText: 'Übernehmen'
    };

    this.dialog
      .open(ConfirmDialogComponent, { data, maxWidth: '560px' })
      .afterClosed()
      .subscribe(bestaetigt => {

        if (!bestaetigt || !this.sammelArt) {
          return;
        }

        this.sammelSpeichert = true;
        this.sammelFehler = '';

        this.service
          .sammelZuordnung(liste.id, this.baueSammelRegeln(regeln), false)
          .subscribe({

            next: ergebnis => {
              this.sammelSpeichert = false;

              this.snackBar.open(
                `${ergebnis.geaendert} Messstelle${ergebnis.geaendert === 1 ? '' : 'n'} geändert.`,
                'OK',
                { duration: 4000 }
              );

              this.sammelSchliessen();

              // Frisch laden: Liste, Filter, Fortschritt, Kategorien und
              // Anlagenteile kommen so aus demselben Stand.
              this.ladeListe(liste.id);
            },

            error: err => {
              this.sammelSpeichert = false;
              this.sammelFehler =
                err?.error?.message ?? 'Die Zuordnung konnte nicht übernommen werden.';
            }

          });
      });
  }


  // ========================================================
  // SIGNALSTATUS UND GESAMTERGEBNIS
  //
  // Beides nur als kompakter Chip: der Signalstatus sagt, wie
  // weit die Bearbeitung ist, das Prüfergebnis, ob die Messstelle
  // fachlich bestanden hat. Der Fortschritt daneben sagt nur,
  // wie viele Prüfpunkte abgearbeitet sind.
  // ========================================================

  signalStatusText(
    messstelle: Messstelle
  ): string {

    return signalStatusText(messstelle.signalStatus);
  }


  signalStatusCss(
    messstelle: Messstelle
  ): string {

    return signalStatusCss(messstelle.signalStatus);
  }


  pruefergebnisText(
    messstelle: Messstelle
  ): string {

    return pruefergebnisText(messstelle.pruefergebnis);
  }


  pruefergebnisCss(
    messstelle: Messstelle
  ): string {

    return pruefergebnisCss(messstelle.pruefergebnis);
  }


  /** Nur ein bereits gefälltes Urteil verdient einen eigenen Chip. */
  hatPruefergebnis(
    messstelle: Messstelle
  ): boolean {

    return (
      normalizePruefergebnis(messstelle.pruefergebnis)
      !== Pruefergebnis.NichtGeprueft
    );
  }


  // ========================================================
  // STATUS
  // ========================================================

  private getStatus(
    messstelle: Messstelle
  ): Exclude<
    MessstellenStatusFilter,
    'alle'
  > {

    const gesamt =
      messstelle.pruefpunkteGesamt
      ?? 0;


    const erledigt =
      messstelle.pruefpunkteErledigt
      ?? 0;


    if (
      gesamt === 0
    ) {

      return 'nichtGestartet';

    }


    if (
      erledigt >= gesamt
    ) {

      return 'geprueft';

    }


    return 'offen';
  }


  statusText(
    messstelle: Messstelle
  ): string {

    const status =
      this.getStatus(
        messstelle
      );


    switch (status) {

      case 'nichtGestartet':
        return 'Nicht gestartet';

      case 'geprueft':
        return 'Geprüft';

      default:
        return 'Offen';
    }
  }


  isPruefungOffen(
    messstelle: Messstelle
  ): boolean {

    return (
      this.getStatus(
        messstelle
      )
      === 'offen'
    );
  }


  isNichtGestartet(
    messstelle: Messstelle
  ): boolean {

    return (
      this.getStatus(
        messstelle
      )
      === 'nichtGestartet'
    );
  }


  isPruefungFertig(
    messstelle: Messstelle
  ): boolean {

    return (
      this.getStatus(
        messstelle
      )
      === 'geprueft'
    );
  }


  // ========================================================
  // PRÜFFORTSCHRITT
  // ========================================================

  pruefStand(
    messstelle: Messstelle
  ): string {

    const gesamt =
      messstelle.pruefpunkteGesamt
      ?? 0;


    if (
      gesamt === 0
    ) {

      return '–';

    }


    return `${messstelle.pruefpunkteErledigt
      ?? 0
      } / ${gesamt}`;
  }


  pruefProzent(
    messstelle: Messstelle
  ): number {

    const gesamt =
      messstelle.pruefpunkteGesamt
      ?? 0;


    const erledigt =
      messstelle.pruefpunkteErledigt
      ?? 0;


    if (
      gesamt === 0
    ) {

      return 0;

    }


    return Math.round(
      (
        erledigt
        /
        gesamt
      )
      *
      100
    );
  }


  // ========================================================
  // NAVIGATION
  // ========================================================

  openMessstelle(
    messstelle: Messstelle
  ): void {

    this.router.navigate(
      [
        '/wartung/inbetriebnahme/messstelle',
        messstelle.id
      ],

      /*
       * Die Herkunft weiterreichen. Sonst landete man von der Messstelle über
       * zweimal „Zurück" auf der Inbetriebnahme-Übersicht statt wieder im
       * Projekt, aus dem man gekommen ist.
       */
      { queryParams: this.herkunftParameter }
    );
  }


  trackByMessstelle(
    _index: number,
    messstelle: Messstelle
  ): number {

    return messstelle.id;
  }


  trackByProjekt(
    _index: number,
    projekt: Projekt
  ): number {

    return projekt.id;
  }


  // ========================================================
  // HERKUNFT
  //
  // Eine Inbetriebnahme lässt sich von zwei Seiten aus öffnen: über
  // Wartung → Inbetriebnahme oder über ein Projekt. „Zurück" muss
  // dorthin führen, wo man hergekommen ist – sonst landet man beim
  // Arbeiten am Projekt nach jeder Messstelle in einem anderen Bereich.
  //
  // Als Query-Parameter und nicht im Router-State: so übersteht die
  // Herkunft ein Neuladen der Seite, was auf einem Tablet oft genug
  // vorkommt.
  // ========================================================

  /** Projekt, aus dem heraus geöffnet wurde. `null` beim Weg über die Übersicht. */
  private get vonProjektId(): number | null {

    const roh =
      this.route
        .snapshot
        .queryParamMap
        .get('vonProjekt');

    const id = Number(roh);

    return roh && !Number.isNaN(id) && id > 0
      ? id
      : null;
  }


  /** Beschriftung des Zurück-Knopfes – nennt das tatsächliche Ziel. */
  get zurueckText(): string {

    return this.vonProjektId != null
      ? 'Zurück zum Projekt'
      : 'Zurück zur Übersicht';
  }


  /** Die Herkunft zum Weiterreichen an die Messstellen-Detailseite. */
  get herkunftParameter(): { [key: string]: number } {

    const id = this.vonProjektId;

    return id != null
      ? { vonProjekt: id }
      : {};
  }


  back(): void {

    const projektId =
      this.vonProjektId;


    if (projektId != null) {

      this.router.navigate([
        '/projekte',
        projektId
      ]);

      return;
    }


    this.router.navigate([
      '/wartung/inbetriebnahme'
    ]);
  }
}